import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import type {
  LearnSection,
  LearnTopic,
} from '@/client/types/learn';
import { useQuery } from '@tanstack/react-query';

const LEARN = '/api/admin/learn';

export const learnKeys = {
  all: ['learn-admin'] as const,
  topics: (includeUnpublished = true) =>
    [...learnKeys.all, 'topics', includeUnpublished] as const,
  topic: (id: string | null) => [...learnKeys.all, 'topic', id] as const,
  section: (slug: string | null) => [...learnKeys.all, 'section', slug] as const,
};

const unwrapEnvelope = (raw: any): any => {
  if (raw != null && typeof raw === 'object' && 'status' in raw && 'data' in raw) {
    return raw.data;
  }
  return raw;
};

const unwrapTopics = (raw: any): LearnTopic[] => {
  const payload = unwrapEnvelope(raw);
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.topics)) return payload.topics;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(raw?.topics)) return raw.topics;
  if (Array.isArray(raw?.items)) return raw.items;
  return [];
};

const unwrapTopic = (raw: any): LearnTopic | null => {
  const payload = unwrapEnvelope(raw);
  if (!payload || typeof payload !== 'object') return null;
  return (payload.topic ?? payload) as LearnTopic;
};

const unwrapSection = (raw: any): LearnSection | null => {
  const payload = unwrapEnvelope(raw);
  if (!payload || typeof payload !== 'object') return null;
  return (payload.section ?? payload) as LearnSection;
};

export const useAdminLearnTopicsQuery = (includeUnpublished = true, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: learnKeys.topics(includeUnpublished),
    enabled,
    queryFn: withErrorHandling(async (): Promise<LearnTopic[]> => {
      const raw = await request<any>(
        `${LEARN}/topics?include_unpublished=${includeUnpublished}`
      );
      return unwrapTopics(raw);
    }, 'Failed to load Learn topics'),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useAdminLearnTopicQuery = (id: string | null, enabled = true) => {
  const request = useClientStore((s) => s.request);
  const hasId = Boolean(id?.trim());
  return useQuery({
    queryKey: learnKeys.topic(id),
    enabled: hasId && enabled,
    queryFn: withErrorHandling(async (): Promise<LearnTopic | null> => {
      if (!id) return null;
      const raw = await request<any>(`${LEARN}/topics/${encodeURIComponent(id.trim())}`);
      return unwrapTopic(raw);
    }, 'Failed to load Learn topic details'),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useAdminLearnSectionQuery = (slug: string | null, enabled = true) => {
  const request = useClientStore((s) => s.request);
  const hasSlug = Boolean(slug?.trim());
  return useQuery({
    queryKey: learnKeys.section(slug),
    enabled: hasSlug && enabled,
    queryFn: withErrorHandling(async (): Promise<LearnSection | null> => {
      if (!slug) return null;
      const raw = await request<any>(`${LEARN}/sections/${encodeURIComponent(slug.trim())}`);
      return unwrapSection(raw);
    }, 'Failed to load Learn section details'),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};
